import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PRODUCTS } from '../config/columns.config';
import { formatMoney } from '../utils/formatters';
import { calculateSalesTotals, calculatePurchaseTotals, calculateAccountTotals } from './calculations';
import { buildSummaryRows } from './summaryCalculations';
import { buildTerritorySummary } from './territorySummaryCalculations';

const LOGO_URL = '/assets/images/logo/LOGO-old.png';

const n = (v) => (typeof v === 'number' ? v : 0);

const COLORS = {
    HEADER_BG: '111827',
    HEADER_TEXT: 'EAAA05',
    MAIN_ROW_BG: '312E81',
    MAIN_ROW_TEXT: 'FFFFFF',
    TOTAL_ROW_BG: '1F2937',
    TOTAL_ROW_TEXT: 'D1D5DB',
    DETAIL_ROW_BG: '252A30',
    DETAIL_ROW_TEXT: '9CA3AF',
    BORDER_COLOR: '4B5563'
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB');
};

const calculateProRata = (total, budget, config) => {
    if (!budget || !config?.dateRange?.start || !config?.dateRange?.end) return 0;

    const startDate = new Date(config.dateRange.start);
    const endDate = new Date(config.dateRange.end);
    const today = new Date();

    const totalDuration = endDate - startDate;
    if (totalDuration <= 0) return 0;

    let timeElapsed = 0;
    if (today < startDate) timeElapsed = 0;
    else if (today > endDate) timeElapsed = totalDuration;
    else timeElapsed = today - startDate;

    const totalDays = Math.ceil(totalDuration / (1000 * 60 * 60 * 24)) + 1;
    const daysPassed = Math.ceil(timeElapsed / (1000 * 60 * 60 * 24)) + 1;

    const proRataBudget = (budget / totalDays) * daysPassed;
    return proRataBudget > 0 ? (total / proRataBudget) * 100 : 0;
};

const imgToBase64 = (url) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.setAttribute('crossOrigin', 'anonymous');
        img.src = url;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL('image/png');
            resolve(dataURL);
        };
        img.onerror = (error) => reject(error);
    });
};

const flattenAccountsWithStyle = (accounts, config, rows = [], level = 0) => {
    accounts.forEach(acc => {
        const indent = '    '.repeat(level);
        const isSales = config?.reportCategory === 'sales';

        const showDetails = config.reportType === 'sales_report_with_details' || config.reportType === 'purchase_report_with_details';
        const showSubAccounts = (config.printMainAccountOnly === 'no') && acc.subAccounts && acc.subAccounts.length > 0;

        const accountTotals = calculateAccountTotals(acc, config);

        const productTotals = PRODUCTS.map(p => {
            return (accountTotals.retail?.[p.key] || 0) + (accountTotals.project?.[p.key] || 0);
        });

        let rowBudget = config?.budgetType === 'internal' ? (acc.internalBudget || 0) : (acc.externalBudget || 0);
        if (acc.subAccounts && acc.subAccounts.length > 0) {
            acc.subAccounts.forEach(sub => {
                rowBudget += config?.budgetType === 'internal' ? (sub.internalBudget || 0) : (sub.externalBudget || 0);
            });
        }

        const grandTotal = accountTotals.grandTotal;
        const pctAchieved = rowBudget ? (grandTotal / rowBudget) * 100 : 0;
        const proRata = calculateProRata(grandTotal, rowBudget, config);

        const rowValues = [
            `${indent}${acc.id} - ${acc.name}`,
            ...productTotals,
            grandTotal,
        ];
        if (isSales) {
            rowValues.push(rowBudget, `${pctAchieved.toFixed(2)}%`, `${proRata.toFixed(2)}%`);
        }
        rows.push({
            type: level === 0 ? 'MAIN' : 'SUB',
            values: rowValues
        });

        if (!showDetails) {
            if (showSubAccounts && acc.subAccounts?.length) {
                flattenAccountsWithStyle(acc.subAccounts, config, rows, level + 1);
            }
            return;
        }

        const hasRetailTotal = true;
        if (hasRetailTotal) {
            const retailValues = PRODUCTS.map(p => (accountTotals.retail?.[p.key] || 0));
            const retailRow = [`${indent}    Total Retail`, ...retailValues, accountTotals.retailTotal];

            let rowRetailBudget = acc.retailBudget || 0;
            if (acc.subAccounts?.length) {
                acc.subAccounts.forEach(sub => rowRetailBudget += (sub.retailBudget || 0));
            }

            if (isSales) {
                const retPct = rowRetailBudget ? (accountTotals.retailTotal / rowRetailBudget) * 100 : 0;
                const retProRata = calculateProRata(accountTotals.retailTotal, rowRetailBudget, config);
                retailRow.push(rowRetailBudget, `${retPct.toFixed(2)}%`, `${retProRata.toFixed(2)}%`);
            }
            rows.push({ type: 'TOTAL_ROW', values: retailRow });

            const retailOnlyTotal = accountTotals.retailOnlyTotal || 0;
            if (retailOnlyTotal > 0 || (acc.retail && Object.keys(acc.retail).length > 0)) {
                const retailOnlyVals = PRODUCTS.map(p => accountTotals.retailOnly?.[p.key] || 0);
                const rawRow = [`${indent}        Retail`, ...retailOnlyVals, retailOnlyTotal];
                if (isSales) rawRow.push('', '', '');
                rows.push({ type: 'DETAIL', values: rawRow });
            }

            const showroomOnlyTotal = accountTotals.showroomOnlyTotal || 0;
            if (showroomOnlyTotal > 0 || (acc.showroom && Object.keys(acc.showroom).length > 0)) {
                const showroomOnlyVals = PRODUCTS.map(p => accountTotals.showroomOnly?.[p.key] || 0);
                const rawRow = [`${indent}        Showroom`, ...showroomOnlyVals, showroomOnlyTotal];
                if (isSales) rawRow.push('', '', '');
                rows.push({ type: 'DETAIL', values: rawRow });
            }
        }

        const hasProjectTotal = true;
        if (hasProjectTotal) {
            const projectValues = PRODUCTS.map(p => (accountTotals.project?.[p.key] || 0));
            const projectRow = [`${indent}    Total Project`, ...projectValues, accountTotals.projectTotal];

            let rowProjectBudget = acc.projectBudget || 0;
            if (acc.subAccounts?.length) {
                acc.subAccounts.forEach(sub => rowProjectBudget += (sub.projectBudget || 0));
            }

            if (isSales) {
                const prjPct = rowProjectBudget ? (accountTotals.projectTotal / rowProjectBudget) * 100 : 0;
                const prjProRata = calculateProRata(accountTotals.projectTotal, rowProjectBudget, config);
                projectRow.push(rowProjectBudget, `${prjPct.toFixed(2)}%`, `${prjProRata.toFixed(2)}%`);
            }
            rows.push({ type: 'TOTAL_ROW', values: projectRow });
            if (!showSubAccounts) {
                (acc.projects || []).forEach(p => {
                    const values = PRODUCTS.map(pr => n(p[pr.key]));
                    const pTotal = values.reduce((a, b) => a + b, 0);
                    const projectLabel = p.id === 'GEN-PRJ' ? 'Projects' : `${p.id} - ${p.name}`;
                    const detailRow = [`${indent}        ${projectLabel}`, ...values, pTotal];
                    if (isSales) detailRow.push('', '', '');
                    rows.push({ type: 'DETAIL', values: detailRow });
                });
            }
        }

        if (showSubAccounts && acc.subAccounts?.length) {
            flattenAccountsWithStyle(acc.subAccounts, config, rows, level + 1);
        }
    });

    return rows;
};

const applySheetStyles = (ws, structuredRows, isSales, headerOffset = 0) => {
    const range = XLSX.utils.decode_range(ws['!ref']);

    for (let R = headerOffset; R <= range.e.r; R++) {
        const isHeader = R === headerOffset;
        const rowMetadata = R > headerOffset ? structuredRows[R - (headerOffset + 1)] : null;

        for (let C = 0; C <= range.e.c; C++) {
            const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
            if (!ws[cellAddress]) continue;

            const cellStyle = {
                font: { name: 'Arial', sz: 10 },
                alignment: { vertical: 'center' },
                border: {
                    top: { style: 'thin', color: { rgb: COLORS.BORDER_COLOR } },
                    bottom: { style: 'thin', color: { rgb: COLORS.BORDER_COLOR } },
                    left: { style: 'thin', color: { rgb: COLORS.BORDER_COLOR } },
                    right: { style: 'thin', color: { rgb: COLORS.BORDER_COLOR } }
                }
            };

            if (isHeader) {
                cellStyle.fill = { fgColor: { rgb: COLORS.HEADER_BG } };
                cellStyle.font = { bold: true, color: { rgb: COLORS.HEADER_TEXT }, sz: 11 };
                cellStyle.alignment.horizontal = 'center';
            }
            else if (rowMetadata) {
                if (C > 0) {
                    if (isSales && (C === range.e.c || C === range.e.c - 1)) {
                        cellStyle.alignment.horizontal = 'right';
                    } else {
                        ws[cellAddress].z = '#,##0.00';
                        cellStyle.alignment.horizontal = 'right';
                    }
                }

                switch (rowMetadata.type) {
                    case 'MAIN':
                        cellStyle.fill = { fgColor: { rgb: COLORS.MAIN_ROW_BG } };
                        cellStyle.font = { bold: true, color: { rgb: COLORS.MAIN_ROW_TEXT } };
                        break;
                    case 'SUB':
                        cellStyle.fill = { fgColor: { rgb: COLORS.DETAIL_ROW_BG } };
                        cellStyle.font = { bold: true, color: { rgb: COLORS.TOTAL_ROW_TEXT } };
                        break;
                    case 'TOTAL_ROW':
                        cellStyle.fill = { fgColor: { rgb: COLORS.TOTAL_ROW_BG } };
                        cellStyle.font = { italic: true, color: { rgb: COLORS.TOTAL_ROW_TEXT } };
                        break;
                    default:
                        cellStyle.fill = { fgColor: { rgb: COLORS.DETAIL_ROW_BG } };
                        cellStyle.font = { color: { rgb: COLORS.DETAIL_ROW_TEXT } };
                        break;
                }
            }
            ws[cellAddress].s = cellStyle;
        }
    }
};

export const exportToExcel = (data, fileName, config) => {
    const isSales = config?.reportCategory === 'sales';
    const wb = XLSX.utils.book_new();

    const reportTitle = fileName.replace('.xlsx', '');
    const dateRangeStr = `Period: ${formatDate(config.dateRange.start)} - ${formatDate(config.dateRange.end)}`;
    const headerRows = [
        [reportTitle],
        [dateRangeStr],
        ['']
    ];

    const totals = isSales ? calculateSalesTotals(data, config) : calculatePurchaseTotals(data);
    const summaryData = buildSummaryRows(totals, config);
    const territoryData = buildTerritorySummary(data, config);
    const mainRows = flattenAccountsWithStyle(data, config);
    const tableHeader = ['Customer', ...PRODUCTS.map(p => p.label), 'Total'];
    if (isSales) tableHeader.push('Budget', '% Achieved', '% Pro Rata');
    const mainSheetData = [...headerRows, tableHeader, ...mainRows.map(r => r.values)];
    const wsMain = XLSX.utils.aoa_to_sheet(mainSheetData);

    applySheetStyles(wsMain, mainRows, isSales, 3);
    if (wsMain['A1']) wsMain['A1'].s = { font: { bold: true, sz: 14, color: { rgb: "000000" } } };
    if (wsMain['A2']) wsMain['A2'].s = { font: { italic: true, sz: 11 } };

    const colWidthsMain = [{ wch: 50 }, ...PRODUCTS.map(() => ({ wch: 18 })), { wch: 20 }];
    if (isSales) colWidthsMain.push({ wch: 20 }, { wch: 15 }, { wch: 15 });
    wsMain['!cols'] = colWidthsMain;

    XLSX.utils.book_append_sheet(wb, wsMain, 'Main Report');

    const summaryHeader = ['Category', ...PRODUCTS.map(p => p.label), 'Total'];
    if (isSales) summaryHeader.push('Budget', '% Achieved', '% Pro Rata');

    const summarySheetRows = summaryData.map(row => {
        const rowVals = [row.label, ...PRODUCTS.map(p => row.values[p.key] || 0), row.total];
        if (isSales) rowVals.push(row.budget, `${row.percentageAchieved.toFixed(2)}%`, `${row.proRataAchieved.toFixed(2)}%`);
        return { type: 'MAIN', values: rowVals };
    });

    const wsSummary = XLSX.utils.aoa_to_sheet([summaryHeader, ...summarySheetRows.map(r => r.values)]);
    applySheetStyles(wsSummary, summarySheetRows, isSales, 0);
    wsSummary['!cols'] = colWidthsMain;
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    const territoryHeader = ['Territory', 'Category', ...PRODUCTS.map(p => p.label), 'Total'];
    if (isSales) territoryHeader.push('Budget', '% Achieved', '% Pro Rata');

    const territorySheetRows = [];
    territoryData.forEach(t => {
        t.rows.forEach((row, idx) => {
            const pct = row.budget ? (row.total / row.budget) * 100 : 0;
            const proRata = calculateProRata(row.total, row.budget, config);

            const rowVals = [
                idx === 0 ? t.territory : '',
                row.label,
                ...PRODUCTS.map(p => row.values[p.key] || 0),
                row.total
            ];
            if (isSales) rowVals.push(row.budget, `${pct.toFixed(2)}%`, `${proRata.toFixed(2)}%`);

            territorySheetRows.push({ type: row.label === 'Total' ? 'TOTAL_ROW' : 'DETAIL', values: rowVals });
        });
        territorySheetRows.push({ type: 'SPACER', values: [] });
    });

    const wsTerritory = XLSX.utils.aoa_to_sheet([territoryHeader, ...territorySheetRows.map(r => r.values)]);
    applySheetStyles(wsTerritory, territorySheetRows, isSales, 0);

    const colWidthsTerritory = [{ wch: 20 }, { wch: 20 }, ...PRODUCTS.map(() => ({ wch: 18 })), { wch: 20 }];
    if (isSales) colWidthsTerritory.push({ wch: 20 }, { wch: 15 }, { wch: 15 });
    wsTerritory['!cols'] = colWidthsTerritory;

    XLSX.utils.book_append_sheet(wb, wsTerritory, 'Territory Summary');

    XLSX.writeFile(wb, fileName);
};

export const exportToPDF = async (data, title, config) => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const isSales = config?.reportCategory === 'sales';
    const totals = isSales ? calculateSalesTotals(data, config) : calculatePurchaseTotals(data);
    const summaryData = buildSummaryRows(totals, config);
    const territoryData = buildTerritorySummary(data, config);

    const formatRow = (row) => row.map(v => typeof v === 'number' ? formatMoney(v) : v);

    const mainRows = flattenAccountsWithStyle(data, config)
        .map(r => r.values)
        .map(formatRow);

    const headers = ['Customer', ...PRODUCTS.map(p => p.label), 'Total'];
    if (isSales) headers.push('Budget', '% Achieved', '% Pro Rata');

    try {
        const logoBase64 = await imgToBase64(LOGO_URL);
        doc.addImage(logoBase64, 'PNG', 700, 20, 100, 40);
    } catch (err) {
        console.warn("Could not load logo for PDF", err);
    }

    const tableStyles = {
        fontSize: 7,
        cellPadding: 3,
        overflow: 'linebreak',
    };
    const headStyles = {
        fillColor: [31, 41, 55],
        textColor: [234, 170, 5],
        fontStyle: 'bold',
    };
    const colStyles = { 0: { cellWidth: 150 } };

    doc.setFontSize(16);
    doc.text(title, 20, 40);
    doc.setFontSize(10);
    doc.setTextColor(100);
    const dateRangeStr = `Reporting Period: ${formatDate(config.dateRange.start)} - ${formatDate(config.dateRange.end)}`;
    doc.text(dateRangeStr, 20, 55);

    autoTable(doc, {
        head: [headers],
        body: mainRows,
        startY: 70,
        styles: tableStyles,
        headStyles: headStyles,
        columnStyles: colStyles,
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 15, right: 15 },
    });

    doc.addPage();
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text("Summary", 20, 25);

    const summaryHeaders = ['Category', ...PRODUCTS.map(p => p.label), 'Total'];
    if (isSales) summaryHeaders.push('Budget', '% Achieved', '% Pro Rata');

    const summaryBody = summaryData.map(row => {
        const rowVals = [row.label, ...PRODUCTS.map(p => row.values[p.key] || 0), row.total];
        if (isSales) rowVals.push(row.budget, `${row.percentageAchieved.toFixed(2)}%`, `${row.proRataAchieved.toFixed(2)}%`);
        return rowVals.map(v => typeof v === 'number' ? formatMoney(v) : v);
    });

    autoTable(doc, {
        head: [summaryHeaders],
        body: summaryBody,
        startY: 40,
        styles: tableStyles,
        headStyles: headStyles,
        columnStyles: { 0: { cellWidth: 100 } },
    });

    doc.text("Territory Summary", 20, doc.lastAutoTable.finalY + 30);

    const territoryHeaders = ['Territory', 'Category', ...PRODUCTS.map(p => p.label), 'Total'];
    if (isSales) territoryHeaders.push('Budget', '% Achieved', '% Pro Rata');

    const territoryBody = [];
    territoryData.forEach(t => {
        t.rows.forEach((row, idx) => {
            const pct = row.budget ? (row.total / row.budget) * 100 : 0;
            const proRata = calculateProRata(row.total, row.budget, config);
            const rowVals = [
                idx === 0 ? t.territory : '',
                row.label,
                ...PRODUCTS.map(p => row.values[p.key] || 0),
                row.total
            ];
            if (isSales) rowVals.push(row.budget, `${pct.toFixed(2)}%`, `${proRata.toFixed(2)}%`);

            territoryBody.push(rowVals.map(v => typeof v === 'number' ? formatMoney(v) : v));
        });
    });

    autoTable(doc, {
        head: [territoryHeaders],
        body: territoryBody,
        startY: doc.lastAutoTable.finalY + 45,
        styles: tableStyles,
        headStyles: headStyles,
        columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 80 } },
    });

    doc.save(`${title}.pdf`);
};